/**
 * PÁGINA DE PREMISSAS
 * 
 * Permite editar todas as premissas do valuation:
 * 1. Premissas Macroeconômicas ✅
 * 2. Premissas de Crescimento ✅
 * 3. Premissas de Margens ✅
 * 4. Premissas de WACC ✅
 * 5. Descontos de Negociação ✅
 * 
 * FASE 4: Integração com DRE Editável ✅
 * - Sincronização automática DRE → Valuation
 * - Seletor de versão Master/Editável
 * - Comparação de cenários
 */

import { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  Snackbar,
  Paper,
  Breadcrumbs,
  Link,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  Refresh,
  Calculate,
  Home,
  CheckCircle,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../store';
import {
  updateMacroAssumptions,
  updateCompanyScenario,
  updateCompanyBeta, // M3: Betas editáveis
  updateCompanyCapexNWC, // M2: CAPEX/NWC editáveis
  updateDiscounts,
  updateStake,
  updateBaseYearFromDRE, // ✅ ANOS DINÂMICOS
} from '../store/slices/valuationSlice';
import { DEFAULT_MACRO_ASSUMPTIONS } from '../core/ValuationEngine.v2';
import { touchFriendlyButton } from '../utils/mobileFriendlyStyles';

// FASE 4: Componentes DRE
import { useDREValuation } from '../hooks/useDREValuation';
import { VersionSelector } from '../components/dre/VersionSelector';
import { DREDebugPanel } from '../components/debug/DREDebugPanel';
import { SaveStatusIndicator } from '../components/dre/SaveStatusIndicator';
import { UnifiedEditButton } from '../components/layout/UnifiedEditButton';

// SPRINT 3: Import versionStore para associar premissas
import { useVersionStore } from '../stores/versionStore';
import type { PremissasSnapshot } from '../types/version';

import { MacroAssumptionsForm } from '../components/forms/MacroAssumptionsForm';
import { GrowthAssumptionsForm } from '../components/forms/GrowthAssumptionsForm';
import { MarginAssumptionsForm, type MarginAssumptionsData } from '../components/forms/MarginAssumptionsForm';
import CapexNWCForm, { type CapexNWCData } from '../components/forms/CapexNWCForm';
import { WACCPorEmpresaForm, type WACCPorEmpresaData } from '../components/forms/WACCPorEmpresaForm';
import { DiscountsAssumptionsForm, type DiscountsAssumptions } from '../components/forms/DiscountsAssumptionsForm';
import { StakeForm, type StakeFormData } from '../components/forms/StakeForm';
import { ValidationPanel } from '../components/audit/ValidationPanel';
import { KeBreakdownCard } from '../components/audit/KeBreakdownCard';
import { useValuationValidations, useHasCriticalErrors } from '../hooks/useValuationValidations';
import type { MacroAssumptions, CompanyAssumptions } from '../types/valuation';
import { RecalcIndicator } from '../components/RecalcIndicator';

// ============================================================================
// TYPES
// ============================================================================

// Interface PremissasState não está sendo usada atualmente
// pois migramos para Redux state management
// Mantida aqui para referência futura se necessário
// interface PremissasState {
//   macro: MacroAssumptions;
//   setima: Pick<CompanyAssumptions, 'crescimento'>;
//   studio555: Pick<CompanyAssumptions, 'crescimento'>;
//   margensSetima: MarginAssumptionsData;
//   margensStudio555: MarginAssumptionsData;
//   wacc: WACCPorEmpresaData;
//   descontos: DiscountsAssumptions;
// }

// ============================================================================
// P0: HELPER - SINCRONIZAÇÃO DE CENÁRIOS
// ============================================================================

/**
 * P0 Fix: Gera variantes dos 3 cenários (Pessimista/Base/Otimista) 
 * sincronizadas proporcionalmente.
 * 
 * Problema Original:
 * - Handlers atualizavam apenas cenário 'base' (hardcoded)
 * - Dashboard podia exibir 'pessimista' ou 'otimista'
 * - Cenários divergentes → parecem não propagar
 * 
 * Solução:
 * - Quando usuário edita Base, atualizar os 3 cenários proporcionalmente
 * - Pessimista: Crescimento × 0.70, Margens × 0.85
 * - Base: Valores editados pelo usuário (sem ajuste)
 * - Otimista: Crescimento × 1.50, Margens × 1.10
 * 
 * @param baseScenario - Cenário base com valores editados pelo usuário
 * @returns Objeto com 3 cenários sincronizados { pessimista, base, otimista }
 * 
 * @example
 * const { pessimista, base, otimista } = generateScenarioVariants({
 *   crescimento: { 2025: 0.15, 2026: 0.12, ... },
 *   margemEBITDA: { 2025: 0.18, 2026: 0.18, ... }
 * });
 * 
 * // Resultado:
 * // pessimista.crescimento[2025] = 0.105 (15% × 0.7)
 * // base.crescimento[2025] = 0.15 (valor original)
 * // otimista.crescimento[2025] = 0.225 (15% × 1.5)
 */
function generateScenarioVariants(
  baseScenario: {
    crescimento: CompanyAssumptions['crescimento'];
    margemEBITDA: CompanyAssumptions['margemEBITDA'];
    margemBruta?: CompanyAssumptions['margemEBITDA'];
    margemLiquida?: CompanyAssumptions['margemEBITDA'];
  }
): {
  pessimista: typeof baseScenario;
  base: typeof baseScenario;
  otimista: typeof baseScenario;
} {
  // Fatores de ajuste calibrados com defaults do ValuationEngine
  const FACTORS = {
    pessimista: {
      crescimento: 0.70,  // Reduz 30% (cenário conservador)
      margem: 0.85,       // Reduz 15% (pressão competitiva)
    },
    otimista: {
      crescimento: 1.50,  // Aumenta 50% (expansão acelerada)
      margem: 1.10,       // Aumenta 10% (eficiência operacional)
    }
  };

  /**
   * Helper: Aplica fator multiplicador a todos os valores de um objeto
   * Preserva as chaves (anos) e multiplica apenas os valores
   */
  const ajustarValores = (
    obj: Record<string | number, number>, 
    factor: number
  ): Record<string | number, number> => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, val]) => [key, val * factor])
    );
  };

  // Gerar cenário pessimista
  const pessimista: typeof baseScenario = {
    crescimento: ajustarValores(baseScenario.crescimento, FACTORS.pessimista.crescimento) as CompanyAssumptions['crescimento'],
    margemEBITDA: ajustarValores(baseScenario.margemEBITDA, FACTORS.pessimista.margem) as CompanyAssumptions['margemEBITDA'],
    margemBruta: baseScenario.margemBruta 
      ? ajustarValores(baseScenario.margemBruta, FACTORS.pessimista.margem) as CompanyAssumptions['margemEBITDA']
      : undefined,
    margemLiquida: baseScenario.margemLiquida 
      ? ajustarValores(baseScenario.margemLiquida, FACTORS.pessimista.margem) as CompanyAssumptions['margemEBITDA']
      : undefined,
  };

  // Gerar cenário otimista
  const otimista: typeof baseScenario = {
    crescimento: ajustarValores(baseScenario.crescimento, FACTORS.otimista.crescimento) as CompanyAssumptions['crescimento'],
    margemEBITDA: ajustarValores(baseScenario.margemEBITDA, FACTORS.otimista.margem) as CompanyAssumptions['margemEBITDA'],
    margemBruta: baseScenario.margemBruta 
      ? ajustarValores(baseScenario.margemBruta, FACTORS.otimista.margem) as CompanyAssumptions['margemEBITDA']
      : undefined,
    margemLiquida: baseScenario.margemLiquida 
      ? ajustarValores(baseScenario.margemLiquida, FACTORS.otimista.margem) as CompanyAssumptions['margemEBITDA']
      : undefined,
  };

  // Base permanece inalterado (valores originais do usuário)
  const base: typeof baseScenario = baseScenario;

  return { pessimista, base, otimista };
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function PremissasPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // ✅ LER DO REDUX (não useState)
  const macro = useSelector((state: RootState) => state.valuation.macroAssumptions);
  const setimaScenario = useSelector((state: RootState) => state.valuation.scenarios.setima[state.valuation.currentScenario]);
  const studios555Scenario = useSelector((state: RootState) => state.valuation.scenarios.studios555[state.valuation.currentScenario]);
  const discounts = useSelector((state: RootState) => state.valuation.discounts);
  const stake = useSelector((state: RootState) => state.valuation.stake);
  const isCalculating = useSelector((state: RootState) => state.valuation.loading);
  const snapshot = useSelector((state: RootState) => state.valuation.snapshot); // ✅ P0.1: Para CAPM Cards
  // M2: CAPEX/NWC
  const companyParams = useSelector((state: RootState) => state.valuation.companyParams);
  // lastUpdated disponível mas não usado no momento
  // const lastUpdated = useSelector((state: RootState) => state.valuation.lastUpdated);
  
  // Estado local apenas para UI (não dados)
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // ========================================================================
  // FASE 4: Hook DRE Valuation
  // ========================================================================
  const {
    historicalData,
    isLoading,
    isSynced: _isSynced,
    lastSyncAt: _lastSyncAt,
    activeVersion,
    switchVersion,
  } = useDREValuation({
    debounceMs: 500,
    showNotifications: true,
  });

  // ========================================================================
  // SPRINT 3: versionStore para associar premissas
  // ========================================================================
  const { baseVersionId, versions, updateVersionWithPremissas, loadVersions } = useVersionStore();
  
  // 🐛 BUG #2 FIX: Carregar versões ao montar componente
  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // ========================================================================
  // M4: Extrair Receitas Reais da DRE 2024
  // ========================================================================
  const receitas2024 = useMemo(() => {
    // Buscar dados de 2024 no historical data
    const data2024 = historicalData.find(year => year.year === 2024);
    
    if (data2024 && data2024.setima.receita > 0 && data2024.studio555.receita > 0) {
      console.log('[M4] ✅ Usando receitas reais da DRE 2024:', {
        setima: data2024.setima.receita,
        studios555: data2024.studio555.receita,
      });
      
      return {
        setima: data2024.setima.receita,
        studios555: data2024.studio555.receita,
        source: 'DRE' as const,
      };
    }
    
    // Fallback para valores estimados se DRE não disponível
    console.log('[M4] ⚠️  DRE 2024 não disponível, usando valores estimados');
    return {
      setima: 8_500_000,
      studios555: 5_000_000,
      source: 'estimate' as const,
    };
  }, [historicalData]);

  // 🆕 Detectar último ano histórico da DRE (dinâmico)
  const lastHistoricalYear = useMemo(() => {
    if (!historicalData || historicalData.length === 0) return 2024;
    const years = historicalData.map(d => d.year);
    return Math.max(...years);
  }, [historicalData]);

  const firstProjectionYear = lastHistoricalYear + 1;
  
  console.log('[Premissas] 📅 Anos dinâmicos:', {
    lastHistorical: lastHistoricalYear,
    firstProjection: firstProjectionYear,
    projectionRange: `${firstProjectionYear}-${firstProjectionYear + 4}`
  });

  // ✅ ANOS DINÂMICOS: Atualizar Redux store quando DRE mudar
  useEffect(() => {
    if (historicalData && historicalData.length > 0) {
      // Construir formato esperado pela action updateBaseYearFromDRE
      // 🔧 FIX: Não filtrar por receita > 0, pois anos recém-adicionados podem ter valores zerados
      const setimaYears = historicalData.map(d => ({ year: d.year }));
      const studio555Years = historicalData.map(d => ({ year: d.year }));
      
      dispatch(updateBaseYearFromDRE({
        setima: { years: setimaYears },
        studio555: { years: studio555Years },
      }));
      
      console.log('[Premissas] 🔄 Redux baseYear atualizado:', { 
        setimaYears: setimaYears.map(y => y.year),
        studio555Years: studio555Years.map(y => y.year),
        detectedBaseYear: lastHistoricalYear
      });
    }
  }, [historicalData, lastHistoricalYear, dispatch]);

  // ✅ HANDLERS REDUX - Disparam recálculo automático
  const handleMacroChange = (newMacro: MacroAssumptions) => {
    dispatch(updateMacroAssumptions(newMacro));
    setHasUnsavedChanges(true);
  };

  const handleSetimaChange = (setima: Pick<CompanyAssumptions, 'crescimento'>) => {
    // P0 Fix: Gerar variantes dos 3 cenários sincronizados
    const { pessimista, base, otimista } = generateScenarioVariants({
      crescimento: setima.crescimento,
      margemEBITDA: setimaScenario.margemEBITDA, // Manter margens atuais
      margemBruta: setimaScenario.margemBruta,
      margemLiquida: setimaScenario.margemLiquida
    });

    // ✅ Atualizar os 3 cenários proporcionalmente
    dispatch(updateCompanyScenario({
      empresa: 'setima',
      cenario: 'pessimista',
      updates: { crescimento: pessimista.crescimento }
    }));

    dispatch(updateCompanyScenario({
      empresa: 'setima',
      cenario: 'base',
      updates: { crescimento: base.crescimento }
    }));

    dispatch(updateCompanyScenario({
      empresa: 'setima',
      cenario: 'otimista',
      updates: { crescimento: otimista.crescimento }
    }));

    setHasUnsavedChanges(true);
    
    console.log('[P0 Fix] Sétima - Cenários atualizados:', { 
      pessimista: pessimista.crescimento, 
      base: base.crescimento, 
      otimista: otimista.crescimento 
    });
  };

  const handleStudio555Change = (studio555: Pick<CompanyAssumptions, 'crescimento'>) => {
    // P0 Fix: Gerar variantes dos 3 cenários sincronizados
    const { pessimista, base, otimista } = generateScenarioVariants({
      crescimento: studio555.crescimento,
      margemEBITDA: studios555Scenario.margemEBITDA, // Manter margens atuais
      margemBruta: studios555Scenario.margemBruta,
      margemLiquida: studios555Scenario.margemLiquida
    });

    // ✅ Atualizar os 3 cenários proporcionalmente
    dispatch(updateCompanyScenario({
      empresa: 'studios555',
      cenario: 'pessimista',
      updates: { crescimento: pessimista.crescimento }
    }));

    dispatch(updateCompanyScenario({
      empresa: 'studios555',
      cenario: 'base',
      updates: { crescimento: base.crescimento }
    }));

    dispatch(updateCompanyScenario({
      empresa: 'studios555',
      cenario: 'otimista',
      updates: { crescimento: otimista.crescimento }
    }));

    setHasUnsavedChanges(true);
    
    console.log('[P0 Fix] 555 Studios - Cenários atualizados:', { 
      pessimista: pessimista.crescimento, 
      base: base.crescimento, 
      otimista: otimista.crescimento 
    });
  };

  const handleMargensSetimaChange = (margensSetima: MarginAssumptionsData) => {
    // P0 Fix: Gerar variantes dos 3 cenários sincronizados
    const { pessimista, base, otimista } = generateScenarioVariants({
      crescimento: setimaScenario.crescimento, // Manter crescimento atual
      margemEBITDA: {
        2025: margensSetima.margemEbitda[2025],
        2026: margensSetima.margemEbitda[2026],
        2027: margensSetima.margemEbitda[2027],
        2028: margensSetima.margemEbitda[2028],
        2029: margensSetima.margemEbitda[2029],
      },
      margemBruta: {
        2025: margensSetima.margemBruta[2025],
        2026: margensSetima.margemBruta[2026],
        2027: margensSetima.margemBruta[2027],
        2028: margensSetima.margemBruta[2028],
        2029: margensSetima.margemBruta[2029],
      },
      margemLiquida: {
        2025: margensSetima.margemLiquida[2025],
        2026: margensSetima.margemLiquida[2026],
        2027: margensSetima.margemLiquida[2027],
        2028: margensSetima.margemLiquida[2028],
        2029: margensSetima.margemLiquida[2029],
      }
    });

    // ✅ Atualizar os 3 cenários proporcionalmente
    dispatch(updateCompanyScenario({
      empresa: 'setima',
      cenario: 'pessimista',
      updates: { 
        margemBruta: pessimista.margemBruta,
        margemEBITDA: pessimista.margemEBITDA,
        margemLiquida: pessimista.margemLiquida
      }
    }));

    dispatch(updateCompanyScenario({
      empresa: 'setima',
      cenario: 'base',
      updates: { 
        margemBruta: base.margemBruta,
        margemEBITDA: base.margemEBITDA,
        margemLiquida: base.margemLiquida
      }
    }));

    dispatch(updateCompanyScenario({
      empresa: 'setima',
      cenario: 'otimista',
      updates: { 
        margemBruta: otimista.margemBruta,
        margemEBITDA: otimista.margemEBITDA,
        margemLiquida: otimista.margemLiquida
      }
    }));

    setHasUnsavedChanges(true);
    
    console.log('[P0 Fix] Sétima Margens - Cenários atualizados:', { 
      pessimista: pessimista.margemEBITDA, 
      base: base.margemEBITDA, 
      otimista: otimista.margemEBITDA 
    });
  };

    const handleMargensStudio555Change = (margensStudio555: MarginAssumptionsData) => {
    // P0 Fix: Gerar variantes dos 3 cenários sincronizados
    const { pessimista, base, otimista } = generateScenarioVariants({
      crescimento: studios555Scenario.crescimento, // Manter crescimento atual
      margemEBITDA: {
        2025: margensStudio555.margemEbitda[2025],
        2026: margensStudio555.margemEbitda[2026],
        2027: margensStudio555.margemEbitda[2027],
        2028: margensStudio555.margemEbitda[2028],
        2029: margensStudio555.margemEbitda[2029],
      },
      margemBruta: {
        2025: margensStudio555.margemBruta[2025],
        2026: margensStudio555.margemBruta[2026],
        2027: margensStudio555.margemBruta[2027],
        2028: margensStudio555.margemBruta[2028],
        2029: margensStudio555.margemBruta[2029],
      },
      margemLiquida: {
        2025: margensStudio555.margemLiquida[2025],
        2026: margensStudio555.margemLiquida[2026],
        2027: margensStudio555.margemLiquida[2027],
        2028: margensStudio555.margemLiquida[2028],
        2029: margensStudio555.margemLiquida[2029],
      }
    });

    // ✅ Atualizar os 3 cenários proporcionalmente
    dispatch(updateCompanyScenario({
      empresa: 'studios555',
      cenario: 'pessimista',
      updates: { 
        margemBruta: pessimista.margemBruta,
        margemEBITDA: pessimista.margemEBITDA,
        margemLiquida: pessimista.margemLiquida
      }
    }));

    dispatch(updateCompanyScenario({
      empresa: 'studios555',
      cenario: 'base',
      updates: { 
        margemBruta: base.margemBruta,
        margemEBITDA: base.margemEBITDA,
        margemLiquida: base.margemLiquida
      }
    }));

    dispatch(updateCompanyScenario({
      empresa: 'studios555',
      cenario: 'otimista',
      updates: { 
        margemBruta: otimista.margemBruta,
        margemEBITDA: otimista.margemEBITDA,
        margemLiquida: otimista.margemLiquida
      }
    }));

    setHasUnsavedChanges(true);
    
    console.log('[P0 Fix] 555 Studios Margens - Cenários atualizados:', { 
      pessimista: pessimista.margemEBITDA, 
      base: base.margemEBITDA, 
      otimista: otimista.margemEBITDA 
    });
  };

  // M2: Handlers CAPEX/NWC
  const handleCapexNWCSetimaChange = (data: CapexNWCData) => {
    dispatch(updateCompanyCapexNWC({
      empresa: 'setima',
      capex: data.capex as CompanyAssumptions['capex_pct_receita'],
      nwc: data.nwc as CompanyAssumptions['nwc_pct_delta_receita']
    }));
    setHasUnsavedChanges(true);
  };

  const handleCapexNWCStudio555Change = (data: CapexNWCData) => {
    dispatch(updateCompanyCapexNWC({
      empresa: 'studios555',
      capex: data.capex as CompanyAssumptions['capex_pct_receita'],
      nwc: data.nwc as CompanyAssumptions['nwc_pct_delta_receita']
    }));
    setHasUnsavedChanges(true);
  };

  const handleWACCChange = (wacc: WACCPorEmpresaData) => {
    // WACC é uma premissa macro, atualizar lá
    dispatch(updateMacroAssumptions({
      taxaSelic: wacc.taxaLivreRisco,
      premioRiscoMercado: wacc.premioRiscoMercado,
      premioRiscoBrasil: wacc.premioRiscoBrasil,
    }));
    
    // M3: Atualizar betas editáveis
    dispatch(updateCompanyBeta({ empresa: 'setima', beta: wacc.setima.beta }));
    dispatch(updateCompanyBeta({ empresa: 'studios555', beta: wacc.studios555.beta }));
    
    setHasUnsavedChanges(true);
  };

  const handleDescontosChange = (descontos: DiscountsAssumptions) => {
    dispatch(updateDiscounts({
      kpd_mode: descontos.kpd_mode,
      kpd_rate: descontos.kpd,
      dloc: descontos.dloc,
      dlom: descontos.dlom,
    }));
    setHasUnsavedChanges(true);
  };

  const handleStakeChange = (stakeData: StakeFormData) => {
    dispatch(updateStake({
      percentual: stakeData.percentual,
      descricao: stakeData.descricao,
    }));
    setHasUnsavedChanges(true);
  };

  const handleReset = () => {
    // Reset para valores DEFAULT
    dispatch(updateMacroAssumptions(DEFAULT_MACRO_ASSUMPTIONS));
    // Reset cenários também se necessário
  };

  // ========================================================================
  // VALIDAÇÕES EM TEMPO REAL
  // ========================================================================
  
  // Calcular WACC médio ponderado das duas empresas
  // Sétima Ke = Rf + β_Sétima × (Rm - Rf) + Risco País
  const keSetima = 
    macro.taxaSelic + 
    0.95 * macro.premioRiscoMercado + // Beta Sétima default
    macro.premioRiscoBrasil;
  
  // 555 Ke = Rf + β_555 × (Rm - Rf) + Risco País
  const ke555 = 
    macro.taxaSelic + 
    1.05 * macro.premioRiscoMercado + // Beta 555 default
    macro.premioRiscoBrasil;
  
  // M4: WACC médio ponderado usando receitas reais da DRE 2024
  const receitaTotal = receitas2024.setima + receitas2024.studios555;
  const pesoSetima = receitas2024.setima / receitaTotal;
  const peso555 = receitas2024.studios555 / receitaTotal;
  const waccMedio = keSetima * pesoSetima + ke555 * peso555;
  
  const gPerpetuo = setimaScenario.crescimento.perpetuo || 0.03;

  // Calcular margens médias (projeção 2025)
  const margensValidacao = {
    bruta: 0.62, // Default
    ebitda: setimaScenario.margemEBITDA[2025] || 0.13,
    liquida: 0.095, // Default
  };

  // Executar validações
  const validations = useValuationValidations({
    wacc: waccMedio,
    g: gPerpetuo,
    pib: macro.crescimentoPIB,
    inflacao: macro.inflacao,
    margens: margensValidacao,
  });

  const hasCriticalErrors = useHasCriticalErrors({
    wacc: waccMedio,
    g: gPerpetuo,
    pib: macro.crescimentoPIB,
    inflacao: macro.inflacao,
    margens: margensValidacao,
  });

  // ========================================================================
  // ACTIONS
  // ========================================================================
  const handleSave = () => {
    // ✅ Dados já estão salvos no Redux via dispatches acima
    // O Redux já recalculou automaticamente
    console.log('Premissas salvas no Redux. Recálculo automático executado.');
    setShowSaveSuccess(true);
    setHasUnsavedChanges(false);
    setLastSavedAt(new Date());
  };

  const handleCalculate = () => {
    // SPRINT 3: Capturar snapshot COMPLETO de premissas antes de calcular
    
    // 🐛 BUG #2 FIX: Se baseVersionId for null, usar versão mais recente
    // Isso acontece quando usuário nunca restaurou uma versão
    const targetVersionId = baseVersionId || versions[0]?.id;
    
    if (targetVersionId && companyParams && snapshot) {
      // Extrair WACC dos breakdowns CAPM disponíveis no snapshot
      const waccSetimaRaw = snapshot.setimaCAPM?.waccBreakdown?.wacc;
      const wacc555Raw = snapshot.studios555CAPM?.waccBreakdown?.wacc;
      
      // Normalizar: pode ser number ou {valor, formula}
      const waccSetima = typeof waccSetimaRaw === 'number' ? waccSetimaRaw : (waccSetimaRaw?.valor || 0.12);
      const wacc555 = typeof wacc555Raw === 'number' ? wacc555Raw : (wacc555Raw?.valor || 0.14);

      const premissasSnapshot: PremissasSnapshot = {
        macro: {
          taxaSelic: macro.taxaSelic,
          premioRiscoMercado: macro.premioRiscoMercado,
          premioRiscoBrasil: macro.premioRiscoBrasil,
        },
        setima: {
          beta: companyParams.setima.beta,
          wacc: waccSetima,
          crescimento: setimaScenario.crescimento,
          margemEBITDA: setimaScenario.margemEBITDA,
        },
        studios555: {
          beta: companyParams.studios555.beta,
          wacc: wacc555,
          crescimento: studios555Scenario.crescimento,
          margemEBITDA: studios555Scenario.margemEBITDA,
        },
        discounts: {
          dlom: discounts.dlom || 0.27,
          dloc: discounts.dloc || 0.10,
          total: (discounts.dlom || 0.27) + (discounts.dloc || 0.10),
        },
        stake: {
          savioBertolani: stake.percentual || 0,
          caioPires: 0,
          vicentePadula: 0,
          brunoFaria: 0,
        },
        timestamp: Date.now(),
      };

      // Associar snapshot à versão DRE (usa baseVersionId OU versão mais recente)
      updateVersionWithPremissas(targetVersionId, premissasSnapshot);

      console.log('[SPRINT 3] ✅ Premissas capturadas e associadas à versão DRE', {
        versionId: targetVersionId,
        wasBaseVersionNull: !baseVersionId,
        usedMostRecent: !baseVersionId && !!versions[0],
        timestamp: new Date(premissasSnapshot.timestamp).toLocaleString('pt-BR'),
        beta_setima: premissasSnapshot.setima.beta,
        wacc_setima: (premissasSnapshot.setima.wacc * 100).toFixed(2) + '%',
        beta_555: premissasSnapshot.studios555.beta,
        wacc_555: (premissasSnapshot.studios555.wacc * 100).toFixed(2) + '%',
        selic: (premissasSnapshot.macro.taxaSelic * 100).toFixed(2) + '%',
        dlom: (premissasSnapshot.discounts.dlom * 100).toFixed(2) + '%',
        dloc: (premissasSnapshot.discounts.dloc * 100).toFixed(2) + '%',
        desconto_total: (premissasSnapshot.discounts.total * 100).toFixed(2) + '%',
      });
    } else {
      console.warn('[SPRINT 3] ⚠️  Não foi possível salvar premissas', {
        targetVersionId,
        baseVersionId,
        hasVersions: versions.length > 0,
        hasCompanyParams: !!companyParams,
        hasSnapshot: !!snapshot,
      });
    }

    // Salvar e ir para dashboard
    handleSave();
    
    // ✅ Dashboard já lê do Redux, vai mostrar dados atualizados
    navigate('/dashboard');
  };

  // ========================================================================
  // FASE 4: Loading State
  // ========================================================================
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Carregando dados históricos da DRE...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Dashboard
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 0.5, fontSize: 16 }} />
          Premissas
        </Typography>
      </Breadcrumbs>

      {/* ✅ MOVIDO: ContextPanel agora está integrado no AppHeader (global em todas as páginas) */}

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SettingsIcon sx={{ fontSize: 32 }} />
            Premissas de Valuation
          </Typography>
          
          {/* FASE 5: Recalc Indicator */}
          <RecalcIndicator variant="full" />
        </Box>
        <Typography variant="body1" color="text.secondary">
          Ajuste as premissas para recalcular o valuation DCF em tempo real
        </Typography>

        {/* FASE 4: Version Selector */}
        <Box sx={{ mt: 2 }}>
          <VersionSelector
            activeVersion={activeVersion}
            onVersionChange={switchVersion}
            showLabels={true}
            size="small"
          />
        </Box>

      </Box>

      {/* Alert de mudanças não salvas */}
      {isCalculating && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2">
              Recalculando valuation com novas premissas...
            </Typography>
          </Stack>
        </Alert>
      )}

      {/* Save Status Indicator */}
      {hasUnsavedChanges && !isCalculating && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SaveStatusIndicator
                isSaving={false}
                lastSaved={lastSavedAt}
                hasUnsavedChanges={hasUnsavedChanges}
              />
              <Typography variant="body2">
                Você tem alterações não salvas. Clique em "Salvar Premissas" para persistir as mudanças.
              </Typography>
            </Box>
          </Stack>
        </Alert>
      )}

      {/* DEBUG PANEL - Only in development */}
      {import.meta.env.DEV && <DREDebugPanel activeVersion={activeVersion} />}

      {/* MELHORIA: Validações em Tempo Real */}
      <Box sx={{ mb: 3 }}>
        <ValidationPanel validations={validations} showSuccessful={false} />
      </Box>

      {/* SPRINT 2: Action buttons - Problema #3 CRÍTICO - Botão unificado */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8} md={8}>
            <Button
              variant="contained"
              color="success"
              startIcon={isCalculating ? <CircularProgress size={20} color="inherit" /> : <Calculate />}
              onClick={async () => {
                // Ação atômica: Salvar + Recalcular em uma única operação
                await handleSave();
                await handleCalculate();
              }}
              disabled={isCalculating || hasCriticalErrors}
              fullWidth
              sx={{
                ...touchFriendlyButton,
                fontSize: '1.05rem',
                fontWeight: 600,
                py: 1.5,
              }}
              title={
                hasCriticalErrors
                  ? 'Corrija os erros críticos antes de salvar e calcular'
                  : isCalculating
                  ? 'Salvando e recalculando...'
                  : 'Salvar premissas e recalcular valuation automaticamente'
              }
            >
              {isCalculating ? 'Salvando e Recalculando...' : 'Salvar e Recalcular Valuation'}
            </Button>
            <Typography variant="caption" display="block" textAlign="center" color="text.secondary" mt={0.5}>
              <CheckCircle sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
              Salva automaticamente suas alterações e recalcula o valuation em uma única ação
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            {/* SPRINT 2: Botão unificado "Ver Edições" */}
            <UnifiedEditButton />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Refresh />}
              onClick={handleReset}
              fullWidth
              sx={touchFriendlyButton}
            >
              Reset Padrões
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Formulários */}
      <Stack spacing={3}>
        {/* 1. Macro */}
        <MacroAssumptionsForm
          values={macro}
          onChange={handleMacroChange}
        />

        {/* 2. Crescimento */}
        <GrowthAssumptionsForm
          setimaValues={{ crescimento: setimaScenario.crescimento }}
          studio555Values={{ crescimento: studios555Scenario.crescimento }}
          onSetimaChange={handleSetimaChange}
          onStudio555Change={handleStudio555Change}
          firstProjectionYear={firstProjectionYear}
        />

        {/* 3. Margens */}
        <MarginAssumptionsForm
          setima={{
            margemBruta: { 2025: 0.62, 2026: 0.63, 2027: 0.64, 2028: 0.65, 2029: 0.65 },
            margemEbitda: setimaScenario.margemEBITDA,
            margemLiquida: { 2025: 0.095, 2026: 0.10, 2027: 0.105, 2028: 0.11, 2029: 0.11 }
          }}
          studio555={{
            margemBruta: { 2025: 0.65, 2026: 0.66, 2027: 0.67, 2028: 0.68, 2029: 0.68 },
            margemEbitda: studios555Scenario.margemEBITDA,
            margemLiquida: { 2025: 0.25, 2026: 0.26, 2027: 0.27, 2028: 0.28, 2029: 0.28 }
          }}
          onSetimaChange={handleMargensSetimaChange}
          onStudio555Change={handleMargensStudio555Change}
          firstProjectionYear={firstProjectionYear}
        />

        {/* 3.5. CAPEX e NWC (M2) */}
        {companyParams && (
          <CapexNWCForm
            setimaData={{
              capex: companyParams.setima.capex_pct_receita,
              nwc: companyParams.setima.nwc_pct_delta_receita
            }}
            studios555Data={{
              capex: companyParams.studios555.capex_pct_receita,
              nwc: companyParams.studios555.nwc_pct_delta_receita
            }}
            onSetimaChange={handleCapexNWCSetimaChange}
            onStudio555Change={handleCapexNWCStudio555Change}
            firstProjectionYear={firstProjectionYear}
          />
        )}

        {/* 4. WACC por Empresa */}
        <WACCPorEmpresaForm
          values={{
            taxaLivreRisco: macro.taxaSelic,
            premioRiscoMercado: macro.premioRiscoMercado,
            premioRiscoBrasil: macro.premioRiscoBrasil,
            setima: {
              beta: companyParams.setima.beta,
              wacc: undefined, // Calculado automaticamente
            },
            studios555: {
              beta: companyParams.studios555.beta,
              wacc: undefined, // Calculado automaticamente
            },
            debtToEquity: 0,
            taxaImposto: 0.34,
          }}
          onChange={handleWACCChange}
          receitaSetima={receitas2024.setima}
          receita555={receitas2024.studios555}
        />

        {/* P0.1: CAPM Breakdown Cards */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon sx={{ fontSize: 24 }} />
            Transparência CAPM (P0.1)
          </Typography>
          
          {!snapshot ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Os cards de breakdown CAPM serão exibidos após clicar em <strong>Calcular Valuation</strong>.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {snapshot.setimaCAPM && (
                <KeBreakdownCard
                  empresa="Sétima"
                  capmComponents={snapshot.setimaCAPM.components}
                  keBreakdown={snapshot.setimaCAPM.keBreakdown}
                  waccBreakdown={snapshot.setimaCAPM.waccBreakdown}
                  warnings={snapshot.setimaCAPM.validation?.warnings || []}
                  errors={snapshot.setimaCAPM.validation?.errors || []}
                />
              )}
              {snapshot.studios555CAPM && (
                <KeBreakdownCard
                  empresa="555 Studios"
                  capmComponents={snapshot.studios555CAPM.components}
                  keBreakdown={snapshot.studios555CAPM.keBreakdown}
                  waccBreakdown={snapshot.studios555CAPM.waccBreakdown}
                  warnings={snapshot.studios555CAPM.validation?.warnings || []}
                  errors={snapshot.studios555CAPM.validation?.errors || []}
                />
              )}
            </Stack>
          )}
        </Box>

        {/* 5. Descontos */}
        <DiscountsAssumptionsForm
          discounts={{
            kpd_mode: discounts.kpd_mode,
            kpd: discounts.kpd_rate,
            dloc: discounts.dloc,
            dlom: discounts.dlom,
          }}
          onChange={handleDescontosChange}
        />

        {/* 6. Participação no Investimento */}
        <StakeForm
          values={stake}
          onChange={handleStakeChange}
        />
      </Stack>

      {/* Success Snackbar */}
      <Snackbar
        open={showSaveSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSaveSuccess(false)}
        message="Premissas salvas com sucesso!"
      />
    </Container>
  );
}
